import React from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const computeMmseScores = (register, scale) => {
    if (!register || !scale) return null;
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    const factors = Array.isArray(scale?.factors) ? scale.factors : [];
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    if (!questions.length || !responses.length) return null;

    const questionById = new Map(questions.map(q => [q.id, q]));
    const factorMap = new Map();
    factors.forEach(f => factorMap.set(f.id, f));

    const sectionScores = new Map();

    const getSectionEntry = (sectionName, factorId) => {
        const key = sectionName || factorId || 'Seccion';
        if (!sectionScores.has(key)) {
            sectionScores.set(key, { id: key, name: sectionName || key, score: 0, maxScore: 0 });
        }
        return sectionScores.get(key);
    };

    // Precompute max score per section from option scores
    questions.forEach(q => {
        const sectionName = q?.section || q?.groupName || q?.dimension || factorMap.get(q.scaleFactorId)?.name || null;
        const entry = getSectionEntry(sectionName, q.scaleFactorId);
        const maxQuestionScore = Math.max(
            ...(q.questionOptionScores || []).map(o => toNumberOrNull(o?.score) ?? 0),
            0
        );
        entry.maxScore += Number.isFinite(maxQuestionScore) ? maxQuestionScore : 0;
    });

    let totalScore = 0;

    responses.forEach(resp => {
        const q = questionById.get(resp.scaleQuestionId);
        if (!q) return;
        const sectionName = q?.section || q?.groupName || q?.dimension || factorMap.get(q.scaleFactorId)?.name || null;
        const entry = getSectionEntry(sectionName, q.scaleFactorId);
        const opt = (q.questionOptionScores || []).find(o => {
            const sid = o.scaleOptionId || o.scaleOption?.id || o.id;
            return Number(sid) === Number(resp.scaleOptionId);
        });
        const score = toNumberOrNull(opt?.score ?? resp.score);
        if (score == null) return;
        totalScore += score;
        entry.score += score;
    });

    return {
        totalScore,
        totalMaxScore: Array.from(sectionScores.values()).reduce((sum, s) => sum + (s.maxScore || 0), 0),
        sections: Array.from(sectionScores.values()),
    };
};

export const ScaleResultMMSE = ({
    title = 'Resultados',
    totalScore,
    totalMaxScore,
    sections = [],
    note = 'Nota: Punto de corte - Adulto no geriatrico: 24 | Adulto geriatrico: 20',
    onComputedResult,
    register,
    scale,
}) => {
    const parsedSections = Array.isArray(sections) ? sections : [];
    const derived = React.useMemo(() => computeMmseScores(register, scale), [register, scale]);
    React.useEffect(() => {
            // Usamos onComputedResult, que es lo que recibimos por props
            if (derived && typeof onComputedResult === 'function') {
                onComputedResult(derived); 
            }
        }, [derived, onComputedResult]);
    const fallbackTotalScore = parsedSections.reduce((sum, section) => {
        const score = toNumberOrNull(section?.score) ?? 0;
        return sum + score;
    }, 0);

    const fallbackTotalMax = parsedSections.reduce((sum, section) => {
        const max = toNumberOrNull(section?.maxScore ?? section?.max) ?? 0;
        return sum + max;
    }, 0);

    const displayTotalScore = toNumberOrNull(totalScore) ?? derived?.totalScore ?? fallbackTotalScore;
    const displayTotalMax = toNumberOrNull(totalMaxScore) ?? derived?.totalMaxScore ?? (fallbackTotalMax || null);
    const displaySections = (derived?.sections && derived.sections.length) ? derived.sections : parsedSections;

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>

            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16, color: '#0d1b2a' }}>
                Puntuación Total: {displayTotalScore}/{displayTotalMax ?? 'N/A'}
            </Typography>

            <List style={{ paddingTop: 0 }}>
                {displaySections.map((section, index) => {
                    const score = toNumberOrNull(section?.score) ?? 0;
                    const max = toNumberOrNull(section?.maxScore ?? section?.max);
                    const label = section?.label || section?.name || section?.title || `Seccion ${index + 1}`;

                    return (
                        <ListItem key={section?.id ?? index} disableGutters>
                            <ListItemIcon style={{ minWidth: 24 }}>
                                <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                            </ListItemIcon>
                            <ListItemText
                                primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                                primary={`${label}: ${score}/${max ?? 'N/A'}`}
                            />
                        </ListItem>
                    );
                })}
            </List>

            {!displaySections.length && (
                <Box px={1}>
                    <Typography variant="body2" style={{ color: '#5f6b7a' }}>
                        No hay secciones para mostrar.
                    </Typography>
                </Box>
            )}

            {note && (
                <Box mt={2} ml={1}>
                    <Typography variant="body2" style={{ color: '#0d1b2a', fontStyle: 'italic' }}>
                        {note}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default ScaleResultMMSE;
